'use client';

import useSWR from 'swr';

import { useCurrentMonthScope } from '@/hooks/useCurrentMonthScope';
import { homeApi } from '@/lib/api';
import { calendarScopeQuery } from '@/lib/calendarScope';
import type { SpendingPace } from '@/types';
import { LONG_LIVED_SWR_OPTIONS, useAuthedFetcher } from './swrConfig';

export interface PaceChartModel {
  /** Category axis for the whole cycle: zero-padded day-of-month labels ("01".."31"). */
  dayLabels: string[];
  /** Cumulative spend per day; null after today so the real line stops at "hoje". */
  realSeries: (number | null)[];
  /** Cumulative reference curve across the whole cycle (the API's day-of-month average). */
  baselineSeries: number[];
  /** Known recurring charges from today on; null before today so the dotted line starts at "hoje". */
  projectionSeries: (number | null)[];
  /** Commitment labels per day (empty before today and on days without a charge). */
  projectionLabels: string[][];
  /** X label at today's position — anchors the dashed "hoje" markLine. */
  todayLabel: string;
  /** Fixed y max (~1.05× the largest of baseline, spent and projected) so all lines fit. */
  yMax: number;
}

/**
 * Derives the W2 chart model from the locked `/home/spending-pace` payload:
 * the API sends one cumulative point per elapsed day plus one projected
 * point per remaining day; the chart needs the full cycle on the x-axis with
 * the real series stopping at today, the projection taking over from today
 * and the baseline curve drawn edge-to-edge.
 */
export function selectPaceChart(pace: SpendingPace): PaceChartModel {
  const totalDays = Math.max(1, pace.totalDays);
  const dayLabels = Array.from({ length: totalDays }, (_, i) => String(i + 1).padStart(2, '0'));

  // Place each point by its day-of-month (dates are "yyyy-MM-dd") instead of
  // trusting array order, so a gap in the series can't shift the whole line.
  const realSeries: (number | null)[] = dayLabels.map(() => null);
  for (const point of pace.series) {
    const day = Number(point.date.slice(8, 10));
    if (day >= 1 && day <= totalDays) realSeries[day - 1] = point.cumulative;
  }

  const baselineSeries = pace.baselineSeries.slice(0, totalDays);

  const todayIndex = Math.min(totalDays, Math.max(1, pace.daysElapsed)) - 1;

  // The projection is null before today: its first point IS today (cumulative ==
  // spentToDate), so the dotted line visually continues the real one.
  const projectionSeries: (number | null)[] = dayLabels.map(() => null);
  const projectionLabels: string[][] = dayLabels.map(() => []);
  for (const point of pace.projection) {
    const day = Number(point.date.slice(8, 10));
    if (day - 1 < todayIndex || day > totalDays) continue;
    projectionSeries[day - 1] = point.cumulative;
    projectionLabels[day - 1] = point.labels;
  }

  const peak = Math.max(pace.baselineTotal, pace.spentToDate, pace.projectedTotal, 1);

  return {
    dayLabels,
    realSeries,
    baselineSeries,
    projectionSeries,
    projectionLabels,
    todayLabel: dayLabels[todayIndex],
    yMax: Math.ceil(peak * 1.05),
  };
}

/**
 * W2 · Ritmo de gastos — current-cycle spending pace from
 * `GET /api/home/spending-pace` (server-cached per user, 1h TTL). The key
 * rides the registered `home-widget` prefix (FINANCIAL_DATA_KEY_PREFIXES) so
 * mutation/sync revalidation sweeps refresh this widget too.
 *
 * The cycle — and "hoje" — is the CLIENT's calendar (`calendarScopeQuery`): the server
 * clock is UTC and would roll the cycle hours before the user's month ends. The key
 * carries the local month so an open tab refetches at the local turn of the month.
 */
export function useSpendingPace(): {
  data: SpendingPace | undefined;
  error: unknown;
  isLoading: boolean;
} {
  const authedFetch = useAuthedFetcher();
  const { year, month } = useCurrentMonthScope();

  const { data, error, isLoading } = useSWR<SpendingPace>(
    ['home-widget-spending-pace', year, month],
    () => authedFetch((token) => homeApi.getSpendingPace(token, calendarScopeQuery())),
    LONG_LIVED_SWR_OPTIONS,
  );

  return { data, error, isLoading };
}
